import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Target, Brain, Shield, TrendingUp, Calendar, DollarSign, CheckCircle, AlertTriangle, Download, Mail, Users, BarChart3 } from 'lucide-react';
import type { CompleteAIRoadmapProps, AssessmentResults } from '../types';
import { enhanceAssessmentResults } from '../services/assessmentAnalysis';

const CompleteAIRoadmap: React.FC<CompleteAIRoadmapProps> = ({ assessmentResults: results, contactInfo, onBackToAssessment }) => {
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});
  
  // Use passed results or fallback to sample data
  const baseResults: AssessmentResults = results || {
    companyName: "Your Business",
    overallScore: 6.6,
    businessStage: "Growing",
    primaryQuadrant: "Revenue",
    primaryChallenge: "Growing revenue",
    aiExperience: "intermediate",
    aiBudget: "25k_50k",
    ethicsImportance: "important",
    vitalSigns: {
      team: 6, audience: 8, leads: 7, meetings: 7,
      content: 9, sales: 5, partnerships: 4, profit: 7
    },
    quadrantScores: {
      revenue: 6.5, brand: 6.5, audience: 8.0, operations: 6.0
    },
    recommendations: {
      quickWins: ["Implement AI-powered lead scoring", "Automate follow-up sequences", "Deploy conversation intelligence"],
      aiStrategies: ["Revenue optimization", "Sales process automation", "Customer engagement enhancement"]
    }
  };

  // Enhance results with sophisticated analysis
  const assessmentResults = enhanceAssessmentResults(baseResults);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      // Auto-expand the section when scrolling to it
      setExpandedSections(prev => ({
        ...prev,
        [sectionId]: true
      }));
    }
  };

  const roadmapSections = [
    {
      id: "executive_summary",
      title: "Executive Summary",
      subtitle: "Your AI Transformation at a Glance",
      icon: <Target className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-bold text-blue-900 mb-2">Current State</h4>
              <div className="text-2xl font-bold text-blue-600">{assessmentResults.overallScore}/10</div>
              <p className="text-sm text-blue-700">Business Health Score</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-bold text-green-900 mb-2">Primary Focus</h4>
              <div className="text-2xl font-bold text-green-600">{assessmentResults.primaryQuadrant}</div>
              <p className="text-sm text-green-700">Biggest Impact Area</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-bold text-purple-900 mb-2">90-Day ROI</h4>
              <div className="text-2xl font-bold text-purple-600">
                {assessmentResults.costModel ? `${assessmentResults.costModel.roi.month3}X` : '3X'}
              </div>
              <p className="text-sm text-purple-700">Projected Return</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border-l-4 border-blue-500">
            <h4 className="font-bold text-gray-900 mb-3">Strategic Recommendation</h4>
            <p className="text-gray-700">
              Based on your assessment, you're in the <strong>{assessmentResults.businessStage}</strong> stage with strong brand presence (9/10 content score) 
              but revenue conversion challenges (5/10 sales score). Our recommended approach focuses on AI-powered sales optimization 
              while building systematic operational processes for scale.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "detailed_analysis",
      title: "Detailed Vital Signs Analysis",
      subtitle: "Deep Dive Into Your Business Health",
      icon: <Brain className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          {Object.entries(assessmentResults.quadrantScores).map(([quadrant, score]) => (
            <div key={quadrant} className="border-2 border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-bold capitalize">{quadrant} Quadrant</h4>
                <div className={`text-2xl font-bold ${(score as number) >= 7 ? 'text-green-600' : (score as number) >= 5 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {score as number}/10
                </div>
              </div>
              
              {quadrant === 'revenue' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="font-bold text-lg">{assessmentResults.vitalSigns.leads}</div>
                      <div className="text-sm text-gray-600">Leads</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="font-bold text-lg">{assessmentResults.vitalSigns.meetings}</div>
                      <div className="text-sm text-gray-600">Meetings</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="font-bold text-lg">{assessmentResults.vitalSigns.sales}</div>
                      <div className="text-sm text-gray-600">Sales</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="font-bold text-lg">{assessmentResults.vitalSigns.profit}</div>
                      <div className="text-sm text-gray-600">Profit</div>
                    </div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                    <h5 className="font-bold text-yellow-900 mb-2">Key Insight</h5>
                    <p className="text-yellow-800">
                      Your sales conversion (5/10) is the bottleneck limiting revenue growth despite strong lead generation (7/10). 
                      AI can optimize your sales process with conversation analysis, objection handling, and automated follow-ups.
                    </p>
                  </div>
                </div>
              )}
              
              {quadrant === 'brand' && (
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <h5 className="font-bold text-green-900 mb-2">Strength Area</h5>
                  <p className="text-green-800">
                    Exceptional content presence (9/10) provides strong foundation. Focus on leveraging this for partnership development (4/10) 
                    through AI-powered relationship mapping and automated partnership outreach.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )
    },
    {
      id: "risk_assessment",
      title: "Risk Assessment & Mitigation",
      subtitle: "Identifying Potential Challenges and Solutions",
      icon: <AlertTriangle className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          {assessmentResults.riskAssessment && assessmentResults.riskAssessment.length > 0 ? (
            <div className="space-y-4">
              {assessmentResults.riskAssessment.map((risk, index) => (
                <div key={index} className={`border-2 rounded-lg p-4 ${
                  risk.severity === 'high' ? 'border-red-200 bg-red-50' :
                  risk.severity === 'medium' ? 'border-yellow-200 bg-yellow-50' :
                  'border-green-200 bg-green-50'
                }`}>
                  <div className="flex items-start justify-between mb-2">
                    <h4 className={`font-bold ${
                      risk.severity === 'high' ? 'text-red-900' :
                      risk.severity === 'medium' ? 'text-yellow-900' :
                      'text-green-900'
                    }`}>
                      {risk.risk}
                    </h4>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                      risk.severity === 'high' ? 'bg-red-200 text-red-800' :
                      risk.severity === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-green-200 text-green-800'
                    }`}>
                      {risk.severity.toUpperCase()}
                    </span>
                  </div>
                  <p className={`text-sm mb-3 ${
                    risk.severity === 'high' ? 'text-red-700' :
                    risk.severity === 'medium' ? 'text-yellow-700' :
                    'text-green-700'
                  }`}>
                    <strong>Impact:</strong> {risk.impact}
                  </p>
                  <div className={`p-3 rounded-lg ${
                    risk.severity === 'high' ? 'bg-red-100' :
                    risk.severity === 'medium' ? 'bg-yellow-100' :
                    'bg-green-100'
                  }`}>
                    <p className={`text-sm ${
                      risk.severity === 'high' ? 'text-red-800' :
                      risk.severity === 'medium' ? 'text-yellow-800' :
                      'text-green-800'
                    }`}>
                      <strong>Mitigation Strategy:</strong> {risk.mitigation}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
              <h4 className="font-bold text-green-900 mb-2">Low Risk Profile</h4>
              <p className="text-green-700">
                Your assessment indicates a relatively low risk profile for AI implementation. 
                Your strong business fundamentals provide a solid foundation for successful AI adoption.
              </p>
            </div>
          )}
        </div>
      )
    },
    {
      id: "team_readiness",
      title: "Team Readiness Analysis",
      subtitle: "Organizational Capability Assessment",
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          {assessmentResults.teamReadiness && (
            <>
              <div className="grid md:grid-cols-3 gap-6">
                <div className={`p-6 rounded-lg border-2 ${
                  assessmentResults.teamReadiness.level === 'high' ? 'border-green-200 bg-green-50' :
                  assessmentResults.teamReadiness.level === 'medium' ? 'border-yellow-200 bg-yellow-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <h4 className={`font-bold mb-2 ${
                    assessmentResults.teamReadiness.level === 'high' ? 'text-green-900' :
                    assessmentResults.teamReadiness.level === 'medium' ? 'text-yellow-900' :
                    'text-red-900'
                  }`}>
                    Team Readiness Score
                  </h4>
                  <div className={`text-3xl font-bold ${
                    assessmentResults.teamReadiness.level === 'high' ? 'text-green-600' :
                    assessmentResults.teamReadiness.level === 'medium' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {assessmentResults.teamReadiness.score}/10
                  </div>
                  <p className={`text-sm capitalize ${
                    assessmentResults.teamReadiness.level === 'high' ? 'text-green-700' :
                    assessmentResults.teamReadiness.level === 'medium' ? 'text-yellow-700' :
                    'text-red-700'
                  }`}>
                    {assessmentResults.teamReadiness.level} readiness
                  </p>
                </div>
                
                <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                  <h4 className="font-bold text-blue-900 mb-4">Strengths</h4>
                  <div className="space-y-2">
                    {assessmentResults.teamReadiness.strengths.map((strength, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm text-blue-700">{strength}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-orange-50 p-6 rounded-lg border-2 border-orange-200">
                  <h4 className="font-bold text-orange-900 mb-4">Development Areas</h4>
                  <div className="space-y-2">
                    {assessmentResults.teamReadiness.gaps.map((gap, index) => (
                      <div key={index} className="flex items-start">
                        <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm text-orange-700">{gap}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500">
                <h4 className="font-bold text-purple-900 mb-4">Recommended Actions</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {assessmentResults.teamReadiness.recommendations.map((recommendation, index) => (
                    <div key={index} className="flex items-start">
                      <Target className="w-4 h-4 text-purple-600 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-purple-700">{recommendation}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )
    },
    {
      id: "success_metrics",
      title: "Success Metrics & KPIs",
      subtitle: "Measurable Outcomes and Tracking",
      icon: <BarChart3 className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          {assessmentResults.successMetrics && (
            <>
              <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg border-2 border-blue-200">
                <h4 className="font-bold text-gray-900 mb-4">Key Performance Indicators</h4>
                <p className="text-gray-700 mb-4">
                  These metrics are specifically selected based on your business priorities and current performance gaps.
                </p>
                <div className="grid gap-4">
                  {assessmentResults.successMetrics.map((metric, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-bold text-gray-900">{metric.metric}</h5>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {metric.timeframe}
                        </span>
                      </div>
                      <div className="grid md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">Baseline:</span>
                          <p className="text-gray-800">{metric.baseline}</p>
                        </div>
                        <div>
                          <span className="font-medium text-green-600">Target:</span>
                          <p className="text-green-800 font-bold">{metric.target}</p>
                        </div>
                        <div>
                          <span className="font-medium text-blue-600">Measurement:</span>
                          <p className="text-blue-700">{metric.measurement}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
                <h4 className="font-bold text-yellow-900 mb-3">Measurement Framework</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-yellow-800 mb-2">Weekly Reviews</h5>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Process efficiency metrics</li>
                      <li>• Team adoption rates</li>
                      <li>• Quick win implementations</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-yellow-800 mb-2">Monthly Analysis</h5>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• ROI calculations</li>
                      <li>• Performance vs targets</li>
                      <li>• Strategy adjustments</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )
    },
    {
      id: "cost_modeling",
      title: "Investment Analysis & ROI",
      subtitle: "Financial Planning and Return Projections",
      icon: <DollarSign className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          {assessmentResults.costModel && (
            <>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                  <h4 className="font-bold text-blue-900 mb-4">Investment Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-blue-700">Software & Tools:</span>
                      <span className="font-bold text-blue-900">
                        ${assessmentResults.costModel.breakdownByCategory.software.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Training & Education:</span>
                      <span className="font-bold text-blue-900">
                        ${assessmentResults.costModel.breakdownByCategory.training.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Implementation:</span>
                      <span className="font-bold text-blue-900">
                        ${assessmentResults.costModel.breakdownByCategory.implementation.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Maintenance:</span>
                      <span className="font-bold text-blue-900">
                        ${assessmentResults.costModel.breakdownByCategory.maintenance.toLocaleString()}
                      </span>
                    </div>
                    <hr className="border-blue-300" />
                    <div className="flex justify-between text-lg">
                      <span className="font-bold text-blue-900">Total Investment:</span>
                      <span className="font-bold text-blue-900">
                        ${assessmentResults.costModel.totalInvestment.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-6 rounded-lg border-2 border-green-200">
                  <h4 className="font-bold text-green-900 mb-4">ROI Projections</h4>
                  <div className="space-y-4">
                    <div className="text-center p-4 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {assessmentResults.costModel.roi.month3}x
                      </div>
                      <div className="text-sm text-green-700">3-Month ROI</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {assessmentResults.costModel.roi.month6}x
                      </div>
                      <div className="text-sm text-green-700">6-Month ROI</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {assessmentResults.costModel.roi.month12}x
                      </div>
                      <div className="text-sm text-green-700">12-Month ROI</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500">
                <h4 className="font-bold text-purple-900 mb-3">Financial Timeline</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600">
                      {assessmentResults.costModel.paybackPeriod} months
                    </div>
                    <div className="text-sm text-purple-700">Payback Period</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600">
                      ${Math.round(assessmentResults.costModel.totalInvestment * assessmentResults.costModel.roi.month12).toLocaleString()}
                    </div>
                    <div className="text-sm text-purple-700">Annual Return Value</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600">
                      ${Math.round((assessmentResults.costModel.totalInvestment * assessmentResults.costModel.roi.month12 - assessmentResults.costModel.totalInvestment) / 12).toLocaleString()}
                    </div>
                    <div className="text-sm text-purple-700">Monthly Net Benefit</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-3">Investment Notes</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div>
                    <h5 className="font-medium text-gray-800 mb-1">Budget Considerations:</h5>
                    <ul className="space-y-1">
                      <li>• Costs adjusted for your experience level</li>
                      <li>• Implementation complexity factored in</li>
                      <li>• Phased deployment options available</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-800 mb-1">ROI Drivers:</h5>
                    <ul className="space-y-1">
                      <li>• Process automation savings</li>
                      <li>• Revenue growth acceleration</li>
                      <li>• Operational efficiency gains</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )
    },
    {
      id: "implementation_plan",
      title: "90-Day Implementation Plan",
      subtitle: "Your Step-by-Step Transformation Journey",
      icon: <Calendar className="w-6 h-6" />,
      content: (
        <div className="space-y-8">
          {/* Phase 1 */}
          <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
            <h4 className="text-xl font-bold text-blue-900 mb-4">Phase 1: Foundation (Days 1-30)</h4>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-bold mb-2">Step 1: Identify & Step 2: Interpret</h5>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Complete business process audit and AI readiness assessment</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Map current sales process and identify conversion bottlenecks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Establish baseline metrics and success criteria</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Team training on AI tools and ethical implementation</span>
                  </li>
                </ul>
                <div className="mt-3 p-3 bg-blue-100 rounded">
                  <strong>Key Deliverable:</strong> Complete AI implementation strategy document
                </div>
              </div>
            </div>
          </div>

          {/* Phase 2 */}
          <div className="border-2 border-green-200 rounded-lg p-6 bg-green-50">
            <h4 className="text-xl font-bold text-green-900 mb-4">Phase 2: Optimization (Days 31-60)</h4>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-bold mb-2">Step 3: Streamline</h5>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Implement AI-powered lead scoring and qualification system</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Deploy conversation intelligence for sales calls</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Automate follow-up sequences and objection handling</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Optimize content distribution using AI analytics</span>
                  </li>
                </ul>
                <div className="mt-3 p-3 bg-green-100 rounded">
                  <strong>Expected Impact:</strong> 30% improvement in sales conversion rate
                </div>
              </div>
            </div>
          </div>

          {/* Phase 3 */}
          <div className="border-2 border-purple-200 rounded-lg p-6 bg-purple-50">
            <h4 className="text-xl font-bold text-purple-900 mb-4">Phase 3: Scale (Days 61-90)</h4>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-bold mb-2">Step 4: Customise & Step 5: Predict</h5>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Deploy personalized customer engagement AI</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Implement predictive analytics for revenue forecasting</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Launch AI-powered partnership identification system</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span>Establish continuous optimization and monitoring</span>
                  </li>
                </ul>
                <div className="mt-3 p-3 bg-purple-100 rounded">
                  <strong>Target Achievement:</strong> 10X ROI and sustainable growth systems
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "ai_tools_stack",
      title: "Recommended AI Tools Stack",
      subtitle: "Curated Technology Solutions for Your Business",
      icon: <Brain className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border-2 border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Revenue Optimization Tools</h4>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h5 className="font-bold text-blue-900">HubSpot AI Features</h5>
                  <p className="text-sm text-blue-700 mt-1">Lead scoring and sales automation</p>
                  <div className="text-sm text-blue-600 mt-2">Monthly Cost: $800-1,200</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h5 className="font-bold text-blue-900">Gong.io</h5>
                  <p className="text-sm text-blue-700 mt-1">Conversation intelligence and coaching</p>
                  <div className="text-sm text-blue-600 mt-2">Monthly Cost: $1,000-1,500</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h5 className="font-bold text-blue-900">Clay</h5>
                  <p className="text-sm text-blue-700 mt-1">AI-powered lead research and enrichment</p>
                  <div className="text-sm text-blue-600 mt-2">Monthly Cost: $300-500</div>
                </div>
              </div>
            </div>

            <div className="border-2 border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Operations & Content Tools</h4>
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-bold text-green-900">Notion AI</h5>
                  <p className="text-sm text-green-700 mt-1">Documentation and process automation</p>
                  <div className="text-sm text-green-600 mt-2">Monthly Cost: $200-400</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-bold text-green-900">Jasper AI</h5>
                  <p className="text-sm text-green-700 mt-1">Content creation and brand voice consistency</p>
                  <div className="text-sm text-green-600 mt-2">Monthly Cost: $400-600</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-bold text-green-900">Zapier</h5>
                  <p className="text-sm text-green-700 mt-1">Workflow automation and integration</p>
                  <div className="text-sm text-green-600 mt-2">Monthly Cost: $300-500</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-bold text-yellow-900 mb-3">Total Technology Investment</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <div className="text-2xl font-bold text-yellow-700">$3,000-5,000</div>
                <div className="text-sm text-yellow-600">Monthly recurring cost</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-700">$36K-60K</div>
                <div className="text-sm text-yellow-600">Annual investment</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-700">10X ROI</div>
                <div className="text-sm text-yellow-600">Expected return</div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "roi_projections",
      title: "ROI Projections & Success Metrics",
      subtitle: "Quantified Business Impact Analysis",
      icon: <DollarSign className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border-2 border-green-200">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Financial Impact Projection</h4>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-700">Current Monthly Revenue:</span>
                  <span className="font-bold">$150,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">AI Implementation Cost:</span>
                  <span className="font-bold text-red-600">$50,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">30% Sales Improvement:</span>
                  <span className="font-bold text-green-600">+$45,000/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Annual Additional Revenue:</span>
                  <span className="font-bold text-green-600">+$540,000</span>
                </div>
                <hr className="border-gray-300" />
                <div className="flex justify-between text-xl">
                  <span className="font-bold">12-Month ROI:</span>
                  <span className="font-bold text-green-600">980%</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border-2 border-purple-200">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Operational Efficiency Gains</h4>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-700">Time Savings (hrs/week):</span>
                  <span className="font-bold">40 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Cost per Hour Saved:</span>
                  <span className="font-bold">$75</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Weekly Savings:</span>
                  <span className="font-bold text-purple-600">$3,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Annual Savings:</span>
                  <span className="font-bold text-purple-600">$156,000</span>
                </div>
                <hr className="border-purple-300" />
                <div className="flex justify-between text-xl">
                  <span className="font-bold">Total Annual Value:</span>
                  <span className="font-bold text-purple-600">$696,000</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
            <h4 className="font-bold text-blue-900 mb-3">Success Metrics Dashboard</h4>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <div className="text-2xl font-bold text-blue-600">30%</div>
                <div className="text-sm text-gray-600">Sales Conversion Increase</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <div className="text-2xl font-bold text-green-600">50%</div>
                <div className="text-sm text-gray-600">Lead Quality Improvement</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <div className="text-2xl font-bold text-purple-600">60%</div>
                <div className="text-sm text-gray-600">Process Efficiency Gain</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <div className="text-2xl font-bold text-orange-600">25%</div>
                <div className="text-sm text-gray-600">Customer Satisfaction Boost</div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "risk_mitigation",
      title: "Risk Mitigation & Ethical Guidelines",
      subtitle: "SymbioEthical Framework Implementation",
      icon: <Shield className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
            <h4 className="font-bold text-red-900 mb-3">
              <AlertTriangle className="w-5 h-5 inline mr-2" />
              Identified Risks & Mitigation Strategies
            </h4>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-bold text-gray-900">Team Adoption Resistance</h5>
                <p className="text-gray-700 text-sm mt-1">Risk: Staff reluctance to adopt AI tools</p>
                <p className="text-green-700 text-sm mt-2"><strong>Mitigation:</strong> Comprehensive training program with clear benefit demonstration</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-bold text-gray-900">Data Privacy Concerns</h5>
                <p className="text-gray-700 text-sm mt-1">Risk: Customer data security and compliance issues</p>
                <p className="text-green-700 text-sm mt-2"><strong>Mitigation:</strong> GDPR-compliant tools with encryption and audit trails</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <h5 className="font-bold text-gray-900">Over-dependence on AI</h5>
                <p className="text-gray-700 text-sm mt-1">Risk: Losing human touch in customer relationships</p>
                <p className="text-green-700 text-sm mt-2"><strong>Mitigation:</strong> Human-in-the-loop approach with AI augmentation, not replacement</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
            <h4 className="font-bold text-green-900 mb-3">SymbioEthical Framework Compliance</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <strong>Goal-Oriented & Process Driven</strong>
                    <p className="text-sm text-gray-600">Clear business objectives with systematic implementation</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <strong>Cognitive Load Reduction</strong>
                    <p className="text-sm text-gray-600">AI handles routine tasks, humans focus on strategy</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <strong>Meaningful Problem Solving</strong>
                    <p className="text-sm text-gray-600">Addressing real business challenges with measurable impact</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <strong>Transparency & Diversity</strong>
                    <p className="text-sm text-gray-600">Open AI decision-making with diverse perspectives</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <strong>Symbiosis & Homeostasis</strong>
                    <p className="text-sm text-gray-600">Human-AI collaboration with sustainable practices</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "next_steps",
      title: "Your Next Steps",
      subtitle: "Ready to Begin Your AI Transformation?",
      icon: <TrendingUp className="w-6 h-6" />,
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-brand-red to-brand-gray text-white p-8 rounded-xl">
            <h4 className="text-2xl font-bold mb-4">Ready to Implement This Roadmap?</h4>
            <p className="text-red-100 mb-6">
              This roadmap provides the strategy - but implementation requires expertise, tools, and ongoing optimization. 
              AmplifyAI Solutions provides comprehensive implementation support to help you execute this plan and achieve measurable results.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                <h5 className="font-bold mb-2">Implementation Services:</h5>
                <ul className="text-sm space-y-1">
                  <li>• 90-day systematic implementation</li>
                  <li>• Expert strategy consultation</li>
                  <li>• AI tools setup & integration</li>
                  <li>• Performance optimization</li>
                  <li>• Ongoing support & training</li>
                </ul>
              </div>
              <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                <h5 className="font-bold mb-2">Get Started:</h5>
                <div className="text-lg font-bold mb-2">Book Strategy Call</div>
                <div className="text-sm">Expert consultation to refine your roadmap</div>
                <div className="text-sm">Custom implementation planning</div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 border-2 border-brand-red rounded-lg">
              <h4 className="text-lg font-bold text-brand-black mb-4">Schedule Your Strategy Call</h4>
              <p className="text-brand-gray mb-4">
                Discuss your specific situation with our AI implementation experts and get personalized guidance for your roadmap.
              </p>
              <a 
                href="https://calendar.app.google/jzemi7eLSzsWJpWi6" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full bg-brand-red text-brand-white py-3 px-6 rounded-lg font-bold hover:bg-red-700 transition-colors flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Your Strategy Call
              </a>
              <p className="text-sm text-brand-gray mt-2 text-center">Expert consultation - no sales pressure</p>
            </div>

            <div className="bg-white p-6 border-2 border-brand-beige rounded-lg">
              <h4 className="text-lg font-bold text-brand-black mb-4">AmplifyAI Solutions</h4>
              <p className="text-brand-gray mb-4">
                Expert AI implementation services to help you transform your business with confidence and measurable results.
              </p>
              <a 
                href="https://amplifyais.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full bg-brand-gray text-brand-white py-3 px-6 rounded-lg font-bold hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Visit amplifyais.com
              </a>
              <p className="text-sm text-brand-gray mt-2 text-center">Comprehensive AI transformation support</p>
            </div>
          </div>

          <div className="bg-brand-beige/10 p-6 rounded-lg border-2 border-brand-beige">
            <h4 className="font-bold text-brand-black mb-3">AmplifyAI Solutions - Contact Information</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-brand-red mr-3" />
                <div>
                  <div className="font-medium text-brand-black">Email</div>
                  <a href="mailto:ronsley@amplifyais.com" className="text-sm text-brand-red hover:underline">ronsley@amplifyais.com</a>
                </div>
              </div>
              <div className="flex items-center">
                <Calendar className="w-5 h-5 text-brand-red mr-3" />
                <div>
                  <div className="font-medium text-brand-black">Strategy Call</div>
                  <a href="https://calendar.app.google/jzemi7eLSzsWJpWi6" target="_blank" rel="noopener noreferrer" className="text-sm text-brand-red hover:underline">Book Your Call</a>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-brand-beige">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="font-medium text-brand-black">Website</div>
                  <a href="https://amplifyais.com" target="_blank" rel="noopener noreferrer" className="text-brand-red hover:underline font-semibold">amplifyais.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white p-8 rounded-xl shadow-lg mb-8">
        <div className="flex justify-between items-start mb-4">
          <button 
            onClick={onBackToAssessment}
            className="text-blue-600 hover:text-blue-700 flex items-center text-sm font-medium"
          >
            ← Back to Assessment
          </button>
        </div>
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Your Complete AI Implementation Roadmap</h1>
          <p className="text-xl text-gray-600 mb-2">
            Personalized Strategy for {contactInfo?.firstName} {contactInfo?.lastName}
            {contactInfo?.companyUrl && (
              <span className="block text-lg text-gray-500 mt-1">
                Company: <a href={contactInfo.companyUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {contactInfo.companyUrl.replace(/^https?:\/\//, '')}
                </a>
              </span>
            )}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Based on the Amazon Best-Selling "Amplify AI" Book by Ronsley Seriojo Vaz
          </p>
          <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
            <DollarSign className="w-4 h-4 mr-2" />
            Worth $2,500 in Consulting Value - SymbioEthical Framework
          </div>
        </div>
        
        <div className="mt-6 flex justify-center">
          <button 
            onClick={() => window.print()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center"
          >
            <Download className="w-5 h-5 mr-2" />
            Download PDF Version
          </button>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Table of Contents</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {roadmapSections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className="flex items-center p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="text-blue-600 mr-3">{section.icon}</div>
              <div>
                <div className="font-medium text-gray-900">{index + 1}. {section.title}</div>
                <div className="text-sm text-gray-600">{section.subtitle}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
            </button>
          ))}
        </div>
      </div>

      {/* Roadmap Sections */}
      <div className="space-y-6">
        {roadmapSections.map((section, index) => (
          <div key={section.id} id={`section-${section.id}`} className="bg-white rounded-xl shadow-lg overflow-hidden">
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="text-blue-600 mr-4">{section.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{index + 1}. {section.title}</h3>
                    <p className="text-gray-600">{section.subtitle}</p>
                  </div>
                </div>
                <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform ${
                  expandedSections[section.id] ? 'rotate-180' : ''
                }`} />
              </div>
            </button>
            
            {expandedSections[section.id] && (
              <div className="p-6 border-t border-gray-200">
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Book Reference Section */}
      <div className="mt-12 bg-white p-8 rounded-xl shadow-lg text-center border border-brand-beige/20">
        <h3 className="text-xl font-bold text-brand-black mb-4">📚 About the Amplify AI Framework</h3>
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-gray mb-4">
            This roadmap is based on the comprehensive methodologies outlined in <strong>"Amplify AI: Integrating Intelligence, Preserving Humanity"</strong> 
            by computer scientist and entrepreneur Ronsley Seriojo Vaz.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="bg-brand-beige/10 p-4 rounded-lg">
              <h4 className="font-semibold text-brand-black mb-2">SymbioEthical Framework</h4>
              <p className="text-sm text-brand-gray">Ethical AI integration that preserves humanity while amplifying business potential</p>
            </div>
            <div className="bg-brand-beige/10 p-4 rounded-lg">
              <h4 className="font-semibold text-brand-black mb-2">5.1M+ Downloads</h4>
              <p className="text-sm text-brand-gray">Proven strategies shared across 133 countries through podcasts and training</p>
            </div>
            <div className="bg-brand-beige/10 p-4 rounded-lg">
              <h4 className="font-semibold text-brand-black mb-2">Amazon Best-Seller</h4>
              <p className="text-sm text-brand-gray">Recognized as the definitive guide for ethical AI business implementation</p>
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <a 
              href="https://amplifyais.com/book" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-brand-red text-brand-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              Get the Complete Book
            </a>
            <a 
              href="https://calendar.app.google/jzemi7eLSzsWJpWi6" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-brand-gray text-brand-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Book Strategy Call
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-gray-500 text-sm">
        <p>This roadmap was generated using the Amplify AI Framework from the Amazon Best-Selling Book</p>
        <p>© 2025 AmplifyAI Solutions - SymbioEthical AI Implementation by Ronsley Seriojo Vaz</p>
      </div>
    </div>
  );
};

export default CompleteAIRoadmap;